<template>
    <v-dialog v-model="dialogOpen" max-width="800px">
        <v-card>
            <toolbar-page :title="creatingNewTenant ? 'Create new tenant' : 'Update tenant'" disable-icon>
                <template slot="buttons">
                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" @click="dialogOpen=false">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Close</span>
                    </v-tooltip>
                </template>
            </toolbar-page>

            <validation-observer ref="validationObserver" v-slot="{ invalid }">

                <v-container fluid>
                    <v-row dense>

                        <v-col cols="12" v-if="trialDays > 0 && creatingNewTenant">
                            <v-alert class="body-2" type="info">
                                Trial expires on {{toLocalDate(trialExpiresOn)}}
                            </v-alert>
                        </v-col>

                        <v-col cols="12" v-if="!trialExpiresOn && creatingNewTenant">
                            <v-alert class="body-2" type="success">
                                Your are inviting paying client! GOOD JOB!
                            </v-alert>
                        </v-col>

                        <v-col cols="12" md="6">

                            <validation-provider
                                    name="Panel name"
                                    rules="min:3|max:50|required"
                                    mode="eager"
                                    v-slot="{ errors }">

                                <v-text-field
                                        mode="eager"
                                        v-show="creatingNewTenant"
                                        v-model="data.panel_name"
                                        label="Panel name"
                                        placeholder="Enter panel name"
                                        :error-messages="errors"
                                ></v-text-field>

                            </validation-provider>
                        </v-col>

                        <v-col cols="12" md="6">
                            <validation-provider
                                    name="trial days"
                                    rules="numeric|min_value:0|max_value:90"
                                    mode="eager"
                                    v-slot="{ errors }">

                                <v-text-field
                                        v-show="creatingNewTenant"
                                        v-model="trialDays"
                                        label="Trial days"
                                        :error-messages="errors"
                                ></v-text-field>
                            </validation-provider>
                        </v-col>

                        <v-col cols="12" md="6">

                            <validation-provider
                                    name="administrator name"
                                    rules="required|min:5|max:50"
                                    mode="eager"
                                    v-slot="{ errors }">

                                <v-text-field
                                        v-show="creatingNewTenant"
                                        :error-messages="errors"
                                        v-model="data.contact_name"
                                        label="Administrator's name"
                                        placeholder="Enter "
                                ></v-text-field>
                            </validation-provider>
                        </v-col>

                        <v-col cols="12" md="6">
                            <validation-provider
                                    name="administrator email"
                                    rules="required|email|min:5|max:50"
                                    mode="eager"
                                    v-slot="{ errors }">

                                <v-text-field
                                        v-show="creatingNewTenant"
                                        v-model="data.contact_email"
                                        :error-messages="errors"
                                        label="Administrator's email"
                                        placeholder="Enter "
                                ></v-text-field>

                            </validation-provider>
                        </v-col>

                        <v-col cols="12" md="6">

                            <validation-provider
                                    name="sub domain"
                                    rules="required|alpha_dash|min:3|max:30"
                                    mode="eager"
                                    v-slot="{ errors }">

                                <v-text-field
                                        v-model="domain"
                                        label="Sub domain"
                                        placeholder="Sub domain"
                                        :error-messages="errors"
                                        @input="domainKeyUp"
                                ></v-text-field>

                            </validation-provider>
                        </v-col>

                        <v-col cols="12" md="6">

                            <v-select
                                    v-model="hostedZone"
                                    :items="hostedZones"
                                    label="Domain"
                                    item-text="name"
                                    return-object
                                    placeholder="Select domain"
                            ></v-select>

                        </v-col>

                        <v-col cols="12" md="6" v-show="creatingNewTenant">

                            <v-select
                                    v-model="data.default_locale"
                                    :items="localesOrdered"
                                    label="Default locale"
                                    item-text="code"
                                    item-value="code"
                                    placeholder="Select default locale"
                            ></v-select>

                        </v-col>

                        <v-col cols="12">
                            <v-text-field
                                    v-model="clientUrl"
                                    filled
                                    disabled
                                    label="Client access URL"
                                    hide-details
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12">

                            <v-switch
                                    color="primary"
                                    class="pt-0"
                                    v-show="creatingNewTenant"
                                    v-model="data.demo_mode"
                                    label="Enable simulation mode (generates 100k panelists)"
                            ></v-switch>

                        </v-col>

                        <v-col cols="12" v-show="creatingNewTenant">
                            <v-alert icon="mdi-alert" class="body-2" type="warning">
                                This administrator will receive an email to join the panel. Invitations are valid for 7
                                days
                                which after they expire. Because this administrator is the first and only user there is
                                no
                                way
                                to login and resend the invitation.
                            </v-alert>
                        </v-col>

                        <v-col cols="12" v-show="creatingNewTenant">
                            <v-btn color="primary" :loading="saving" @click="submitData">Create tenant</v-btn>
                        </v-col>

                        <v-col cols="12" v-show="!creatingNewTenant">
                            <v-btn color="primary" :loading="saving" @click="submitData">Update tenant</v-btn>
                        </v-col>

                    </v-row>
                </v-container>

            </validation-observer>

            <json-pretty v-if="debug" :data="data"></json-pretty>
        </v-card>
    </v-dialog>
</template>

<script>

    import JsonPretty from 'vue-json-pretty';
    import ToolbarPage from '../../../components/UI/ToolbarPage';
    import moment from 'moment';
    import {timeDisplay} from "../../../components/mixins/timeDisplayMixin";

    export default {

        mixins: [timeDisplay],

        components: {
            ToolbarPage,
            JsonPretty
        },

        props: {
            value: {
                type: Boolean,
                default: false,
                required: true
            },
            saving: {
                type: Boolean,
                default: false,
                required: false,
            },
            tenant: {
                type: Object,
                required: true,
            },
            locales: {
                type: Array,
                required: true,
            }
        },

        data() {
            return {
                debug: false,
                trialDays: 30,
                hostedZones: [],
                hostedZone: null,
                domain: null,
                data: {
                    uuid: null,
                    panel_name: null,
                    contact_name: null,
                    contact_email: null,
                    fqdn: null,
                    trial_expires_on: null,
                    demo_mode: true,
                    default_locale: 'ENG-US',
                },
                createInProgress: false,
            }
        },

        watch: {

            tenant(tenant) {
                this.data = Object.assign({}, tenant);

                this.domain = null;
                this.hostedZone = {'name': 'sampleninja.io'};

                if (tenant.uuid) {

                    const reg = new RegExp('(^[a-z0-9]+)\.([a-z0-9]+\.[a-z]+$)');
                    const result = reg.exec(tenant.fqdn);

                    this.domain = result[1];
                    this.hostedZone = {name: result[2]}
                }
             },
        },

        computed: {

            localesOrdered() {
                return this.locales.sort((a,b) => {

                    const nameA = a.code.toUpperCase(); // ignore upper and lowercase
                    const nameB = b.code.toUpperCase(); // ignore upper and lowercase

                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }

                    // names must be equal
                    return 0;
                });
            },

            clientUrl() {
                this.data.fqdn =
                    this.domain && this.hostedZone && this.hostedZone.name ?
                        this.domain.toLowerCase() + '.' + this.hostedZone.name : null;

                return this.domain && this.hostedZone && this.hostedZone.name ?
                    'https://' + this.domain + '.' + this.hostedZone.name : 'https://';
            },

            trialExpiresOn() {
                if (this.trialDays > 0) {
                    this.data.trial_expires_on = moment.utc().add(this.trialDays, 'days').format('YYYY-MM-DD');
                    return this.data.trial_expires_on;
                } else {
                    this.data.trial_expires_on = null;
                    return null;
                }
            },

            dialogOpen: {
                get() {
                    if (this.value) this.getHostedZones();
                    return this.value;
                },
                set() {
                    this.closeDialog();
                }
            },

            creatingNewTenant() {
                return this.data.uuid === null;
            }
        },

        methods: {

            closeDialog() {
                this.$emit('input', false);
            },

            async submitData() {

                if (this.$refs.validationObserver) {
                    const isValid = await this.$refs.validationObserver.validate();

                    if (isValid) {
                        this.createInProgress = true;
                        this.$emit('change', this.data);
                    } else {
                        Bus.$emit('flash-message', {type: 'error', message: 'Tenant form contains errors!'});
                    }
                }
            },

            getHostedZones() {
                axios.get('/hosted-zones')
                    .then(({data}) => {
                        this.hostedZones = data.hostedZones;
                    })
                    .catch(({$response}) => {
                        Bus.$emit('server-error');
                    });
            },

            domainKeyUp(value) {
                this.domain = value.toLowerCase();
            }
        },
    }
</script>
